/**
 * "Add property" wizard dialog for the Properties page.
 *
 * A three-step form — Property · Financing · Income — that hand-adds an
 * already-owned property. On submit it delegates to `useAddProperty`, which
 * writes pipeline_properties + property_setting + loans (+ rehab_projects when
 * marked In Rehab) in one sequence.
 *
 * Picking a strategy (or the BRRRR loan slot) re-seeds the financing rate /
 * points / term defaults, mirroring `SLOT_DEFAULTS`.
 */
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { StepFinancing } from "@/components/properties/add-property/StepFinancing";
import { StepIncome } from "@/components/properties/add-property/StepIncome";
import { StepProperty } from "@/components/properties/add-property/StepProperty";
import {
  ADD_PROPERTY_DEFAULTS,
  addPropertySchema,
  STEP_FIELDS,
  STEP_NAMES,
  type AddPropertyFormValues,
} from "@/components/properties/add-property/schema";
import { useAddProperty } from "@/hooks/use-add-property";
import {
  strategyLoanDefaults,
  type AddPropertyForm,
  type BrrrrLoanKind,
} from "@/lib/add-property";
import type { Strategy } from "@/types/pipeline";

interface AddPropertyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddPropertyDialog({ open, onOpenChange }: AddPropertyDialogProps) {
  const { addProperty } = useAddProperty();
  const [step, setStep] = useState(0);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const form = useForm<AddPropertyFormValues>({
    resolver: zodResolver(addPropertySchema),
    defaultValues: ADD_PROPERTY_DEFAULTS,
  });

  const isLast = step === STEP_NAMES.length - 1;

  const applyLoanDefaults = (strategy: Strategy, kind: BrrrrLoanKind) => {
    const d = strategyLoanDefaults(strategy, kind);
    form.setValue("rate", d.rate);
    form.setValue("points", d.points);
    form.setValue("termMonths", d.termMonths);
    form.setValue("termUnit", d.termUnit);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      form.reset(ADD_PROPERTY_DEFAULTS);
      setStep(0);
      setImageFile(null);
    }
    onOpenChange(next);
  };

  const handleNext = async () => {
    const valid = await form.trigger(STEP_FIELDS[step] as (keyof AddPropertyFormValues)[]);
    if (valid) setStep((s) => Math.min(STEP_NAMES.length - 1, s + 1));
  };

  const handleSubmit = form.handleSubmit((values) => {
    addProperty.mutate(
      { form: values as AddPropertyForm, imageFile },
      { onSuccess: () => handleOpenChange(false) },
    );
  });

  const pending = addProperty.isPending;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Add property</DialogTitle>
          <DialogDescription>
            Step {step + 1} of {STEP_NAMES.length} · {STEP_NAMES[step]}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <div className="py-2">
            {step === 0 && (
              <StepProperty
                form={form}
                imageFile={imageFile}
                onImageFileChange={setImageFile}
                onStrategyChange={(strategy) =>
                  applyLoanDefaults(strategy, form.getValues("brrrrLoanKind"))
                }
              />
            )}
            {step === 1 && (
              <StepFinancing
                form={form}
                onBrrrrKindChange={(kind) => applyLoanDefaults("BRRRR", kind)}
              />
            )}
            {step === 2 && <StepIncome form={form} />}
          </div>
        </Form>

        <DialogFooter className="gap-2 sm:justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0 || pending}
          >
            <ArrowLeft className="mr-1 h-4 w-4" /> Back
          </Button>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => handleOpenChange(false)}
              disabled={pending}
            >
              Cancel
            </Button>
            {isLast ? (
              <Button type="button" onClick={handleSubmit} disabled={pending}>
                <Check className="mr-1 h-4 w-4" />
                {pending ? "Adding…" : "Add property"}
              </Button>
            ) : (
              <Button type="button" onClick={handleNext} disabled={pending}>
                Next <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
